import type {
  FieldUpdateProposal,
  FieldUpdateProposalChange,
  FieldUpdateProposalConflict,
  FieldUpdateProposalValue,
} from "@shared/types/form-submission";

type SupportedFieldUpdateProposalSchemaVersion = 1 | 2;

export interface FieldUpdateProposalActionState {
  isPending: boolean;
  hasConflicts: boolean;
  canApply: boolean;
  canReject: boolean;
  canUseItemActions: boolean;
  changeCount: number;
  approvalRequiredCount: number;
  pendingDecisionCount: number;
  approvedCount: number;
  rejectedCount: number;
  autoApprovedCount: number;
  appliedCount: number;
}

export function parseFieldUpdateProposal(
  value: unknown,
): FieldUpdateProposal | null {
  const record = asRecord(value);
  if (!record) return null;

  if (
    !isSupportedSchemaVersion(record.schema_version) ||
    !isProposalStatus(record.status) ||
    typeof record.generated_at !== "string" ||
    typeof record.matched_firm_id !== "string" ||
    typeof record.matched_lawyer_id !== "string"
  ) {
    return null;
  }

  const schemaVersion = record.schema_version;
  const changes = arrayOf(record.changes, (item) =>
    parseChange(item, schemaVersion),
  );
  const conflicted =
    record.conflicted === undefined
      ? []
      : arrayOf(record.conflicted, parseConflict);
  if (!changes || !conflicted) return null;

  return {
    schema_version: schemaVersion,
    status: record.status,
    generated_at: record.generated_at,
    generated_by_account_id:
      typeof record.generated_by_account_id === "string"
        ? record.generated_by_account_id
        : null,
    matched_firm_id: record.matched_firm_id,
    matched_lawyer_id: record.matched_lawyer_id,
    changes,
    conflicted,
    reviewed_by_account_id:
      typeof record.reviewed_by_account_id === "string"
        ? record.reviewed_by_account_id
        : null,
    reviewed_at:
      typeof record.reviewed_at === "string" ? record.reviewed_at : null,
  };
}

export function fieldUpdateProposalActionState(
  proposal: FieldUpdateProposal | null,
): FieldUpdateProposalActionState {
  const isPending = proposal?.status === "pending";
  const hasConflicts = (proposal?.conflicted.length ?? 0) > 0;
  const changes = proposal?.changes ?? [];
  const pendingDecisionCount = changes.filter(
    (change) => change.decision === "pending",
  ).length;
  return {
    isPending,
    hasConflicts,
    canApply: isPending && !hasConflicts && pendingDecisionCount === 0,
    canReject: isPending,
    canUseItemActions: isPending && proposal?.schema_version === 2,
    changeCount: changes.length,
    approvalRequiredCount: changes.filter((change) => change.requires_approval)
      .length,
    pendingDecisionCount,
    approvedCount: changes.filter((change) => change.decision === "approved")
      .length,
    rejectedCount: changes.filter((change) => change.decision === "rejected")
      .length,
    autoApprovedCount: changes.filter(
      (change) => change.decision === "auto_approved",
    ).length,
    appliedCount: changes.filter((change) => change.applied_at !== null)
      .length,
  };
}

function parseChange(
  value: unknown,
  schemaVersion: SupportedFieldUpdateProposalSchemaVersion,
): FieldUpdateProposalChange | null {
  const record = asRecord(value);
  if (!record) return null;
  if (
    !isProposalEntity(record.entity) ||
    typeof record.field !== "string" ||
    !isChangeKind(record.change_kind) ||
    typeof record.requires_approval !== "boolean" ||
    !isProposalValue(record.after)
  ) {
    return null;
  }

  const before =
    record.before === undefined && schemaVersion === 1 ? null : record.before;
  if (!isNullableProposalValue(before)) return null;

  if (
    schemaVersion === 2 &&
    (typeof record.change_id !== "string" ||
      record.change_id.trim().length === 0 ||
      !isDecision(record.decision) ||
      !isNullableString(record.decided_by_account_id) ||
      !isNullableString(record.decided_at) ||
      !isNullableString(record.applied_by_account_id) ||
      !isNullableString(record.applied_at))
  ) {
    return null;
  }

  return {
    change_id:
      typeof record.change_id === "string"
        ? record.change_id
        : `${record.entity}.${record.field}`,
    entity: record.entity,
    field: record.field,
    before,
    after: record.after,
    change_kind: record.change_kind,
    requires_approval: record.requires_approval,
    decision: isDecision(record.decision)
      ? record.decision
      : record.requires_approval
        ? "pending"
        : "auto_approved",
    decided_by_account_id:
      typeof record.decided_by_account_id === "string"
        ? record.decided_by_account_id
        : null,
    decided_at:
      typeof record.decided_at === "string" ? record.decided_at : null,
    applied_by_account_id:
      typeof record.applied_by_account_id === "string"
        ? record.applied_by_account_id
        : null,
    applied_at:
      typeof record.applied_at === "string" ? record.applied_at : null,
  };
}

function parseConflict(value: unknown): FieldUpdateProposalConflict | null {
  const record = asRecord(value);
  if (!record) return null;
  if (!isProposalEntity(record.entity) || typeof record.field !== "string")
    return null;
  return {
    entity: record.entity,
    field: record.field,
  };
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (typeof value !== "object" || value === null || Array.isArray(value))
    return null;
  return value as Record<string, unknown>;
}

function arrayOf<T>(
  value: unknown,
  parser: (item: unknown) => T | null,
): T[] | null {
  if (!Array.isArray(value)) return null;
  const parsed = value.map(parser);
  return parsed.every((item): item is T => item !== null) ? parsed : null;
}

function isProposalStatus(
  value: unknown,
): value is FieldUpdateProposal["status"] {
  return value === "pending" || value === "applied" || value === "rejected";
}

function isSupportedSchemaVersion(
  value: unknown,
): value is SupportedFieldUpdateProposalSchemaVersion {
  return value === 1 || value === 2;
}

function isNullableString(value: unknown): value is string | null {
  return value === null || typeof value === "string";
}

function isProposalValue(value: unknown): value is FieldUpdateProposalValue {
  return (
    typeof value === "string" ||
    (Array.isArray(value) && value.every((item) => typeof item === "string"))
  );
}

function isNullableProposalValue(
  value: unknown,
): value is FieldUpdateProposalValue | null {
  return value === null || isProposalValue(value);
}

function isProposalEntity(
  value: unknown,
): value is FieldUpdateProposalChange["entity"] {
  return value === "firm" || value === "lawyer";
}

function isChangeKind(
  value: unknown,
): value is FieldUpdateProposalChange["change_kind"] {
  return value === "blank_completion" || value === "existing_value_diff";
}

function isDecision(
  value: unknown,
): value is FieldUpdateProposalChange["decision"] {
  return (
    value === "pending" ||
    value === "approved" ||
    value === "rejected" ||
    value === "auto_approved"
  );
}
